import React, { useContext, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { HiHeart, HiOutlineHeart } from "react-icons/hi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/shop.scss";

const sortOptions = [
  { value: "new", label: "New Arrivals" },
  { value: "popular", label: "Most Popular" },
  { value: "price-desc", label: "Price High–Low" },
  { value: "price-asc", label: "Price Low–High" },
];

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toggleWishlist, isWishlisted } = useContext(CartContext);

  const activeCategory = searchParams.get("category") || "All Products";
  const activeSize = searchParams.get("size") || "All";
  const activeSort = searchParams.get("sort") || "new";
  const searchTerm = searchParams.get("search") || "";
  const inStockOnly = searchParams.get("stock") === "true";

  useEffect(() => {
    document.title = `${activeCategory} | ${siteConfig.brandName}`;
  }, [activeCategory]);

  const categories = useMemo(
    () => [
      "All Products",
      "New Arrivals",
      "Women's",
      ...Array.from(new Set(productData.map((product) => product.category))),
    ],
    []
  );

  const sizes = useMemo(
    () => [
      "All",
      ...Array.from(
        new Set(productData.flatMap((product) => product.size || []))
      ).filter((size) => size !== "One Size"),
      "One Size",
    ],
    []
  );

  const visibleProducts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filtered = productData.filter((product) => {
      const matchesCategory =
        activeCategory === "All Products" ||
        activeCategory === "All" ||
        (activeCategory === "New Arrivals"
          ? product.isNew
          : activeCategory === "Women's"
            ? product.tags?.includes("women")
            : product.category === activeCategory);
      const matchesSize =
        activeSize === "All" || product.size?.includes(activeSize);
      const matchesStock = !inStockOnly || Number(product.stock) > 0;
      const matchesSearch =
        !normalizedSearch ||
        [product.name, product.category, ...(product.tags || [])]
          .join(" ")
          .toLowerCase()
          .includes(normalizedSearch);
      return matchesCategory && matchesSize && matchesStock && matchesSearch;
    });

    return [...filtered].sort((first, second) => {
      if (activeSort === "price-desc") return second.price - first.price;
      if (activeSort === "price-asc") return first.price - second.price;
      if (activeSort === "popular") return second.reviews - first.reviews;
      return Number(second.isNew) - Number(first.isNew) || first.id - second.id;
    });
  }, [activeCategory, activeSize, activeSort, inStockOnly, searchTerm]);

  const updateParam = (key, value, defaultValue) => {
    const nextParams = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) nextParams.delete(key);
    else nextParams.set(key, value);
    setSearchParams(nextParams);
  };

  const clearFilters = () => setSearchParams({});
  const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const formatPrice = (price) =>
    new Intl.NumberFormat(siteConfig.currency?.locale || "en-US", {
      style: "currency",
      currency: siteConfig.currency?.code || "USD",
    }).format(price);

  return (
    <main className="shop-page">
      <header className="shop-page__hero">
        <p>THE COLLECTION</p>
        <h1>{searchTerm ? `SEARCH: “${searchTerm}”` : activeCategory}</h1>
        <span>{visibleProducts.length} PRODUCTS</span>
      </header>

      <div className="shop-page__body">
      <div className="shop-page__category-bar" aria-label="Product categories">
        {categories.map((category) => (
          <button
            key={category}
            type="button"
            className={activeCategory === category ? "is-active" : ""}
            onClick={() => updateParam("category", category, "All Products")}
          >
            {category}
          </button>
        ))}
      </div>

      <section className="shop-page__tools" aria-label="Filter and sort products">
        <div className="shop-page__filter-group">
          <label htmlFor="shop-size">SIZE</label>
          <select
            id="shop-size"
            value={activeSize}
            onChange={(event) => updateParam("size", event.target.value, "All")}
          >
            {sizes.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
          <label className="shop-page__stock-filter">
            <input
              type="checkbox"
              checked={inStockOnly}
              onChange={(event) =>
                updateParam("stock", event.target.checked ? "true" : "", "")
              }
            />
            <span aria-hidden="true" />
            IN STOCK ONLY
          </label>
        </div>
        <div className="shop-page__sort-group">
          {(activeSize !== "All" || inStockOnly || searchTerm) && (
            <button type="button" onClick={clearFilters}>CLEAR ALL</button>
          )}
          <label htmlFor="shop-sort">SORT BY</label>
          <select
            id="shop-sort"
            value={activeSort}
            onChange={(event) => updateParam("sort", event.target.value, "new")}
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </section>

      {visibleProducts.length > 0 ? (
        <section className="shop-grid" aria-label="Products">
          {visibleProducts.map((product) => {
            const wished = isWishlisted(product.id);
            const soldOut = Number(product.stock) <= 0;
            return (
              <article className="shop-card" key={product.id}>
                <div className="shop-card__media">
                  <Link to={`/shopDetails/${product.id}`} onClick={toTop}>
                    <img
                      src={product.image}
                      alt={product.name}
                      width="1200"
                      height="1500"
                      loading="lazy"
                      decoding="async"
                      onError={(event) => {
                        event.currentTarget.style.opacity = "0";
                      }}
                    />
                  </Link>
                  <div className="shop-card__badges">
                    {product.isNew && <span>NEW</span>}
                    {soldOut && <span>SOLD OUT</span>}
                  </div>
                  <button
                    type="button"
                    className={wished ? "is-active" : ""}
                    aria-label={wished ? `Remove ${product.name} from wishlist` : `Add ${product.name} to wishlist`}
                    aria-pressed={wished}
                    onClick={() => toggleWishlist(product.id)}
                  >
                    {wished ? <HiHeart /> : <HiOutlineHeart />}
                  </button>
                </div>
                <Link className="shop-card__details" to={`/shopDetails/${product.id}`} onClick={toTop}>
                  <h2>{product.name}</h2>
                  <p>{formatPrice(product.price)}{soldOut && <span> — Sold Out</span>}</p>
                  <small>{product.colors?.length || 1} COLOR{product.colors?.length === 1 ? "" : "S"}</small>
                </Link>
              </article>
            );
          })}
        </section>
      ) : (
        <section className="shop-page__empty" aria-live="polite">
          <p>NO PRODUCTS MATCH THESE FILTERS.</p>
          <h2>Try another size, category, or search.</h2>
          <button type="button" onClick={clearFilters}>CLEAR ALL FILTERS</button>
        </section>
      )}
      </div>
    </main>
  );
}

export default Shop;
